// ABOUTME: WebSocket hook for real-time game room events.
// ABOUTME: Connects to the bingo-api WebSocket, joins the game room, routes events, and reconnects on drop.
import { useEffect, useLayoutEffect, useRef } from 'react'

const WS_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:3001').replace(/^http/, 'ws')

const INITIAL_RETRY_DELAY = 1000
const MAX_RETRY_DELAY = 30000

interface UseGameRoomOptions {
  gameId: string
  onEvent: (event: string, data: unknown) => void
  onReconnect?: () => void
}

export function useGameRoom({ gameId, onEvent, onReconnect }: UseGameRoomOptions): void {
  const onEventRef = useRef(onEvent)
  const onReconnectRef = useRef(onReconnect)
  useLayoutEffect(() => {
    onEventRef.current = onEvent
    onReconnectRef.current = onReconnect
  })

  useEffect(() => {
    let ws: WebSocket
    let retryDelay = INITIAL_RETRY_DELAY
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let hasConnected = false
    let destroyed = false

    function connect() {
      const socket = new WebSocket(WS_URL)
      ws = socket

      socket.onopen = () => {
        if (destroyed) return
        socket.send(JSON.stringify({ event: 'join', data: { gameId } }))
        retryDelay = INITIAL_RETRY_DELAY
        if (hasConnected) {
          onReconnectRef.current?.()
        }
        hasConnected = true
      }

      socket.onmessage = (e: MessageEvent) => {
        try {
          const { event, data } = JSON.parse(e.data as string)
          onEventRef.current(event, data)
        } catch {
          // ignore malformed messages
        }
      }

      socket.onclose = () => {
        if (destroyed) return
        if (socket !== ws) return
        retryTimer = setTimeout(() => {
          connect()
        }, retryDelay)
        retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY)
      }
    }

    function handleVisibility() {
      if (
        document.visibilityState === 'visible' &&
        (retryTimer !== null || (ws?.readyState !== WebSocket.OPEN && ws?.readyState !== WebSocket.CONNECTING))
      ) {
        if (retryTimer !== null) {
          clearTimeout(retryTimer)
          retryTimer = null
        }
        connect()
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)
    connect()

    return () => {
      destroyed = true
      document.removeEventListener('visibilitychange', handleVisibility)
      if (retryTimer !== null) clearTimeout(retryTimer)
      ws?.close()
    }
  }, [gameId])
}
